import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import ClockIcon from '@atlaskit/icon/core/clock';
import CommentIcon from '@atlaskit/icon/core/comment';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import { useAutomationPermissionService } from '@confluence/automation-menu/entry-points/AutomationPermissionService';

import { useDynamicSurfacePanel } from '@atlassian/ai-smart-button';
import type { BaseAIAction } from '@atlassian/ai-smart-button-api';
import { useSourceAIActions } from '@atlassian/ai-smart-button-api';

import { QuickSummaryStreamingComponent } from '@confluence/quick-summary';
import { PageAutomationRovoButton } from '@confluence/automation-menu/entry-points/PageAutomationRovoButton';
import {
	CommentsSummarySource,
	FooterCommentsSummaryCombined,
} from '@confluence/ai-comments-summary';
import { PageCatchupComponent } from '@confluence/page-catchup';
import { CommentCountsContext, CommentCountsProvider } from '@confluence/comment-context';
import { useObjectSidebarActions } from '@confluence/object-sidebar-api';
import { fg } from '@confluence/feature-gating';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import {
	BULK_ISSUE_CREATE_SIDE_PANEL_ID,
	BULK_ISSUE_CREATE_SIDE_PANEL_WIDTH,
	constructInsertStorageFragmentRequestPayload,
	getPageContentRange,
	getValidRange,
	useGetContentLastModifiedTimestamp,
	useSetInsertStorageFragmentRequestPayload,
	useSetIsCreatedFromTable,
	useSetIsCreatedWithAi,
	useWarningDialogStateActions,
	WarningDialog,
} from '@confluence/issue-create-side-panel';
import { convertSelectionToLegacyFormat } from '@confluence/highlight-actions';
import { CatchupContentType } from '../__types__/CatchupLastViewedTimeQuery';

import { useDynamicSplitButton } from './useDynamicSplitButton';
import { useCreateObjectSidebarProp } from './useCreateObjectSidebarProp';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';

import { useBulkIssueCreate } from './useBulkIssueCreate';

export const i18n = defineMessages({
	inlineCommentsMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.inline-comments',
		defaultMessage: 'Summarize comments',
		description: 'Label for the AI Content Actions Inline Comments Menu Item',
	},
	pageCatchupMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.page-catchup',
		defaultMessage: 'Summarize changes',
		description: 'Label for the AI Content Actions Page Catchup Menu Item',
	},
	summarizeSectionLabel: {
		id: 'ai-floating-context-menu.summarize.section.label',
		defaultMessage: 'Summarize',
		description: 'Label for the summarize section in the menu group',
	},
	bulkIssueCreateMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.bulk-issue-create',
		defaultMessage: 'Create Jira issues',
		description: 'Label for the Jira Bulk Issue Create Menu Item',
	},
	bulkWorkItemsCreateMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.bulk-work-items-create',
		defaultMessage: 'Create Jira work items',
		description: 'Label for the Jira Bulk Work Items Create Menu Item',
	},
});

const VIEW_PAGE_SOURCE_ID = 'view-page';

type AIConsumptionViewPageProps = {
	contentId: string;
	contentType: string;
	spaceKey: string;
	contentTitle: string;
	lastModifiedDate?: string | null;
};

export const useAIConsumptionViewPage = ({
	contentId,
	contentType,
	spaceKey,
	contentTitle,
	lastModifiedDate,
}: AIConsumptionViewPageProps) => {
	const { formatMessage } = useIntl();
	const { setActions, setNudges, setAutomationsConfig } = useSourceAIActions(VIEW_PAGE_SOURCE_ID);
	const { unresolvedInlineCommentsCount, footerCommentsCount } = useContext(CommentCountsContext);
	const { showObjectSidebar } = useObjectSidebarActions();
	const pageMode = useGetPageMode();
	const { canManageAutomations } = useAutomationPermissionService(spaceKey);

	const objectSidebar = useCreateObjectSidebarProp();
	const dynamicSurfacePanel = useDynamicSurfacePanel(objectSidebar);
	const rightSidebarContext = useRightSidebarContext();
	const setIsCreatedWithAi = useSetIsCreatedWithAi();
	const setIsCreatedFromTable = useSetIsCreatedFromTable();
	const setInsertStorageFragmentRequestPayload = useSetInsertStorageFragmentRequestPayload();
	const { showWarningDialog } = useWarningDialogStateActions();
	const contentLastModifiedTimestamp = useGetContentLastModifiedTimestamp();
	const contentTypeEnum =
		contentType === 'page' ? CatchupContentType.PAGE : CatchupContentType.BLOGPOST;
	const { showCatchupAsDefault } = useDynamicSplitButton(contentId, contentTypeEnum);
	const isSidePanelOpen =
		rightSidebarContext.getCurrent()?.panelId === BULK_ISSUE_CREATE_SIDE_PANEL_ID;

	const content = useRef<HTMLDivElement | null>(
		typeof document !== 'undefined' ? document.querySelector('div#content') : null,
	);

	const handleBulkIssueCreate = useBulkIssueCreate({
		contentId,
		content,
	});

	const nudges = useMemo(
		() => [
			{
				id: 'summarize-changes-ai-action',
				title: formatMessage(i18n.pageCatchupMenuItemLabel),
				icon: <ClockIcon label="" />,
				onClick: () => showObjectSidebar(dynamicSurfacePanel, 'push'),
				viewConfig: {
					render: () => (
						<PageCatchupComponent
							contentId={contentId}
							contentType={contentType}
							spaceKey={spaceKey}
							isInObjectSidebar
						/>
					),
				},
			},
		],
		[formatMessage, contentId, contentType, spaceKey, dynamicSurfacePanel, showObjectSidebar],
	);

	const onBulkIssueCreate = useCallback(() => {
		if (isSidePanelOpen) {
			showWarningDialog(WarningDialog.FLOATING);
		} else if (fg('jira-work-create-from-connie-without-edit-perms')) {
			void handleBulkIssueCreate();
		} else {
			void setIsCreatedWithAi(true);
			void setIsCreatedFromTable(false);
			const range = getValidRange(getPageContentRange());
			const payload = constructInsertStorageFragmentRequestPayload(
				contentId,
				convertSelectionToLegacyFormat,
				range,
				content.current,
				contentLastModifiedTimestamp,
			);
			void setInsertStorageFragmentRequestPayload(payload);
			rightSidebarContext.open(BULK_ISSUE_CREATE_SIDE_PANEL_ID, BULK_ISSUE_CREATE_SIDE_PANEL_WIDTH);
		}
	}, [
		contentId,
		contentLastModifiedTimestamp,
		isSidePanelOpen,
		rightSidebarContext,
		setInsertStorageFragmentRequestPayload,
		setIsCreatedFromTable,
		setIsCreatedWithAi,
		showWarningDialog,
		handleBulkIssueCreate,
	]);

	const actions = useMemo(() => {
		const baseActions: BaseAIAction[] = [
			{
				id: 'summarize-page-ai-action',
				title: formatMessage(i18n.summarizeSectionLabel),
				icon: <ListBulletedIcon label="" />,
				viewConfig: {
					render: () => (
						<QuickSummaryStreamingComponent
							contentId={contentId}
							spaceKey={spaceKey}
							contentType={contentType}
							lastModifiedDate={lastModifiedDate ?? ''}
							source="view-page"
							setIsOpen={() => {}}
							contentPath={window.location.pathname}
							isInObjectSidebar
						/>
					),
				},
			},
			...nudges,
		];

		if (unresolvedInlineCommentsCount > 0 || footerCommentsCount > 0) {
			baseActions.push({
				id: 'summarize-comments-ai-action',
				title: formatMessage(i18n.inlineCommentsMenuItemLabel),
				icon: <CommentIcon label="" />,
				viewConfig: {
					render: () => (
						<CommentCountsProvider contentId={contentId}>
							<FooterCommentsSummaryCombined
								contentId={contentId}
								contentType={contentType}
								source={CommentsSummarySource.Footer}
								spaceKey={spaceKey}
								isInObjectSidebar
							/>
						</CommentCountsProvider>
					),
				},
			});
		}

		if (pageMode === PageMode.VIEW && fg('confluence_ai_bulk_issue_creation')) {
			baseActions.push({
				id: 'bulk-issue-create-ai-action',
				title: formatMessage(
					fg('confluence-issue-terminology-refresh')
						? i18n.bulkWorkItemsCreateMenuItemLabel
						: i18n.bulkIssueCreateMenuItemLabel,
				),
				icon: <WorkItemsIcon label="" />,
				onClick: onBulkIssueCreate,
			});
		}

		return baseActions;
	}, [
		formatMessage,
		contentId,
		spaceKey,
		contentType,
		lastModifiedDate,
		unresolvedInlineCommentsCount,
		footerCommentsCount,
		nudges,
		onBulkIssueCreate,
		pageMode,
	]);

	useEffect(() => {
		setActions(actions);

		if (contentType === 'page' && fg('confluence_ai_automations_in_rovo_button')) {
			setAutomationsConfig({
				userAutomations: {
					render: () => (
						<PageAutomationRovoButton
							spaceKey={spaceKey}
							pageId={contentId}
							contentTitle={contentTitle}
						/>
					),
				},
				...(canManageAutomations && {
					adminAutomations: {
						onClick: () =>
							window.open(`/wiki/spaces/${spaceKey}/settings/automation#rule/new`, '_blank'),
					},
				}),
			});
		}

		if (showCatchupAsDefault) {
			setNudges(nudges);
		}
	}, [
		actions,
		nudges,
		setActions,
		setNudges,
		showCatchupAsDefault,
		setAutomationsConfig,
		contentType,
		spaceKey,
		contentId,
		contentTitle,
		canManageAutomations,
	]);
};
